import { autosaveStatusEmitter } from '@/hooks/use-autosave-status';
import { type PageEditorAction, PageEditorActionType } from '@/types/page-editor-actions';
import { buildContentRevision } from './autosave-content-revision';
import { remapBlockId, remapParentBlockId, remapSectionId } from './autosave-id-remap';

export const AUTOSAVE_DEBOUNCE_MS = 5_000;
export const AUTOSAVE_RETRY_BASE_MS = 7_000;
export const AUTOSAVE_RETRY_MAX_MS = 45_000;
export const AUTOSAVE_MAX_RETRY_ATTEMPTS = 3;

interface AutosaveContext {
  pageId: string | null;
  pageVersionId: string | null;
  contentRevision: string | null;
}

export interface AutosaveBatchPayload {
  pageId: string;
  actions: PageEditorAction[];
  attempt?: number;
}

export interface AutosaveSaveResponse {
  sectionsMapping?: Record<string, string>;
  blocksMapping?: Record<string, string>;
  hasUnpublishedChanges?: boolean;
  pageVersionId?: string;
  pageVersionUpdatedAt?: string;
}

export interface AutosaveSnapshot {
  pageId: string | null;
  pageVersionId: string | null;
  contentRevision: string | null;
}

interface ResetContentRevisionParams {
  pageId: string | null;
  pageVersionId: string | null;
}

interface MarkAutosaveSavedParams {
  response: AutosaveSaveResponse;
  pageId: string;
}

interface MarkPersistedPageContentChangeParams {
  pageId?: string | null;
  pageVersionId?: string | null;
  pageVersionUpdatedAt?: string;
}

interface RemapPendingAutosaveActionsParams {
  sectionsMapping?: Record<string, string>;
  blocksMapping?: Record<string, string>;
}

interface AutosaveRuntimeState {
  autosaveFlushTimer: ReturnType<typeof setTimeout> | null;
  currentFlushPromise: Promise<void> | null;
  aiOperationPauseCount: number;
  pendingAssetStoreRefresh?: boolean;
}

export const autosaveContext: AutosaveContext = {
  pageId: null,
  pageVersionId: null,
  contentRevision: null,
};

export const autosaveRuntimeState: AutosaveRuntimeState = {
  autosaveFlushTimer: null,
  currentFlushPromise: null,
  aiOperationPauseCount: 0,
  pendingAssetStoreRefresh: false,
};

const pendingAutosaveActions: PageEditorAction[] = [];

export function clearQueuedAutosave(): void {
  if (autosaveRuntimeState.autosaveFlushTimer) {
    clearTimeout(autosaveRuntimeState.autosaveFlushTimer);
    autosaveRuntimeState.autosaveFlushTimer = null;
  }
  pendingAutosaveActions.length = 0;
}

export function queuePendingAutosaveAction(action: PageEditorAction): void {
  pendingAutosaveActions.push(action);
}

export function takePendingAutosaveActions(): PageEditorAction[] {
  return pendingAutosaveActions.splice(0, pendingAutosaveActions.length);
}

export function getQueuedAutosaveCount(): number {
  return pendingAutosaveActions.length;
}

export function hasQueuedAutosaveActions(): boolean {
  return pendingAutosaveActions.length > 0;
}

function resetContentRevision({ pageId, pageVersionId }: ResetContentRevisionParams): void {
  autosaveContext.contentRevision =
    pageId === null
      ? null
      : buildContentRevision({
          pageId,
          pageVersionId,
        });
}

export function markAutosaveSaved({ response, pageId }: MarkAutosaveSavedParams): void {
  if (!pageId || autosaveContext.pageId !== pageId) {
    return;
  }

  if (response.pageVersionId) {
    autosaveContext.pageVersionId = response.pageVersionId;
  }

  autosaveContext.contentRevision = buildContentRevision({
    pageId,
    pageVersionId: autosaveContext.pageVersionId,
    pageVersionUpdatedAt: response.pageVersionUpdatedAt || new Date().toISOString(),
  });
}

export function remapPendingAutosaveActions({
  sectionsMapping = {},
  blocksMapping = {},
}: RemapPendingAutosaveActionsParams): void {
  const hasSectionMappings = Object.keys(sectionsMapping).length > 0;
  const hasBlockMappings = Object.keys(blocksMapping).length > 0;
  if (!hasSectionMappings && !hasBlockMappings) {
    return;
  }

  pendingAutosaveActions.forEach((action) => {
    if (action.type === PageEditorActionType.PAGE_SECTION_REORDER) {
      if (hasSectionMappings) {
        action.payload.sectionIds = action.payload.sectionIds.map((sectionId) =>
          remapSectionId(sectionId, sectionsMapping),
        );
      }
      return;
    }

    if ('sectionId' in action.payload && hasSectionMappings) {
      action.payload.sectionId = remapSectionId(action.payload.sectionId, sectionsMapping);
    }

    if (action.type === PageEditorActionType.PAGE_SECTION_ADD && hasSectionMappings) {
      action.payload.section.id = remapSectionId(action.payload.section.id, sectionsMapping);
    }

    if (action.type === PageEditorActionType.PAGE_BLOCK_ADD && hasBlockMappings) {
      action.payload.blockId = remapBlockId(action.payload.blockId, blocksMapping);
      action.payload.block = {
        ...action.payload.block,
        id: remapBlockId(action.payload.block.id, blocksMapping),
        parentBlockId: remapParentBlockId(action.payload.block.parentBlockId, blocksMapping),
      };
      return;
    }

    if (action.type === PageEditorActionType.PAGE_BLOCK_UPDATE && hasBlockMappings) {
      action.payload.blockId = remapBlockId(action.payload.blockId, blocksMapping);
      action.payload.updates = {
        ...action.payload.updates,
        parentBlockId: remapParentBlockId(action.payload.updates.parentBlockId, blocksMapping),
      };
      if (typeof action.payload.updates.id === 'string') {
        action.payload.updates.id = remapBlockId(action.payload.updates.id, blocksMapping);
      }
      return;
    }

    if (action.type === PageEditorActionType.PAGE_BLOCK_DELETE && hasBlockMappings) {
      action.payload.blockId = remapBlockId(action.payload.blockId, blocksMapping);
    }
  });
}

export function setAutosaveContext(context: Partial<AutosaveContext>) {
  const hasPageId = 'pageId' in context;
  const hasPageVersionId = 'pageVersionId' in context;

  if (hasPageId) {
    const nextPageId = context.pageId ?? null;
    if (autosaveContext.pageId !== nextPageId) {
      autosaveRuntimeState.aiOperationPauseCount = 0;
      // Drop queued actions from the previous page to avoid cross-page autosave writes.
      clearQueuedAutosave();
      autosaveStatusEmitter.setIdle();
      autosaveContext.pageVersionId = null;
      resetContentRevision({ pageId: nextPageId, pageVersionId: null });
    }
    autosaveContext.pageId = nextPageId;
  }

  if (hasPageVersionId) {
    autosaveContext.pageVersionId = context.pageVersionId ?? null;
    if (autosaveContext.pageId) {
      resetContentRevision({
        pageId: autosaveContext.pageId,
        pageVersionId: autosaveContext.pageVersionId,
      });
    }
  }
}

export function getAutosaveSnapshot(pageId?: string | null): AutosaveSnapshot {
  if (pageId && autosaveContext.pageId !== pageId) {
    return {
      pageId,
      pageVersionId: null,
      contentRevision: null,
    };
  }

  return {
    pageId: autosaveContext.pageId,
    pageVersionId: autosaveContext.pageVersionId,
    contentRevision: autosaveContext.contentRevision,
  };
}

/**
 * Marks that page content changed through an already-persisted external update
 * (for example: image-run completion/stream updates applied to store state).
 * This does not queue autosave; it only advances the in-memory revision marker
 * used by assistant request context staleness checks.
 */
export function markPersistedPageContentChange({
  pageId,
  pageVersionId,
  pageVersionUpdatedAt,
}: MarkPersistedPageContentChangeParams = {}): void {
  const targetPageId = pageId ?? autosaveContext.pageId;
  if (!targetPageId) {
    return;
  }

  // Ignore updates for non-active pages.
  if (autosaveContext.pageId && targetPageId !== autosaveContext.pageId) {
    return;
  }

  if (pageVersionId) {
    autosaveContext.pageVersionId = pageVersionId;
  }

  autosaveContext.contentRevision = buildContentRevision({
    pageId: targetPageId,
    pageVersionId: autosaveContext.pageVersionId ?? null,
    pageVersionUpdatedAt: pageVersionUpdatedAt || new Date().toISOString(),
  });
}
